import { Component, Input, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, CarIcon, BatteryIcon } from 'lucide-angular';
import { VehicleService } from '../../../services/vehicle.service';

@Component({
  selector: 'allas-vehicle-battery-recommendation',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    @if (hasDefaultVehicle()) {
    <div
      class="bg-gray-50 border-t-4 border-appPrimary-500 p-4 xl:p-6 mb-6 relative"
    >
      <!-- Mobile Layout: Vertical como en la imagen -->
      <div class="xl:hidden space-y-6">
        <!-- Sección 1: COMPRANDO ACTUALMENTE PARA -->
        <div>
          <span class="font-bold text-black uppercase text-sm block mb-3">
            COMPRANDO ACTUALMENTE PARA:
          </span>
          <div
            class="bg-white border-l-4 border-appPrimary-500 px-4 py-3 flex items-center"
          >
            <lucide-icon
              [name]="carIcon"
              class="w-5 h-5 text-appPrimary-500 mr-3"
            />
            <span class="text-black font-medium">
              {{ getDefaultVehicle()?.anio }} {{ getDefaultVehicle()?.marca }}
              {{ getDefaultVehicle()?.modelo }} {{ getDefaultVehicle()?.motor }}
            </span>
          </div>
        </div>

        <!-- Sección 2: BATERÍA DE TU VEHÍCULO -->
        <div>
          <h3 class="font-bold text-black uppercase text-lg mb-4">
            BATERÍA DE TU VEHÍCULO
          </h3>

          <!-- Especificaciones en columnas verticales para mobile -->
          <div class="space-y-4 mb-4">
            <div class="text-center">
              <span class="text-appPrimary-500 font-bold text-lg block"
                >Grupo 24F</span
              >
              <span class="text-black text-xs">Grupo BCI</span>
            </div>
            <div class="text-center">
              <span class="text-black font-bold text-lg block">650 CCA</span>
              <span class="text-black text-xs">Amperios de Arranque</span>
            </div>
            <div class="text-center">
              <span class="text-appPrimary-500 font-bold text-lg block"
                >12V</span
              >
              <span class="text-black text-xs">Voltaje</span>
            </div>
          </div>

          <!-- Texto descriptivo -->
          <p class="text-black text-sm leading-relaxed">
            Esta es la batería recomendada para tu vehículo actual. Por favor
            consulta tu manual del propietario para información completa. Las
            especificaciones pueden variar según el modelo y año de tu vehículo.
          </p>
        </div>

        <!-- Botones de acción -->
        <div class="flex flex-col space-y-2">
          <button
            (click)="clearDefaultVehicle()"
            class="text-black underline text-sm hover:text-gray-700"
          >
            Cambiar Vehículo
          </button>
          <button
            (click)="clearDefaultVehicle()"
            class="text-black underline text-sm hover:text-gray-700"
          >
            Comprar Sin Vehículo
          </button>
        </div>
      </div>

      <!-- Desktop Layout: Horizontal como antes -->
      <div class="hidden xl:block">
        <!-- Sección Superior: Vehículo Seleccionado -->
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center">
            <span class="font-bold text-black uppercase text-sm mr-4">
              COMPRANDO ACTUALMENTE PARA:
            </span>
            <div
              class="bg-white border-l-4 border-appPrimary-500 px-4 py-3 flex items-center"
            >
              <lucide-icon
                [name]="carIcon"
                class="w-5 h-5 text-appPrimary-500 mr-3"
              />
              <span class="text-black font-medium">
                {{ getDefaultVehicle()?.anio }} {{ getDefaultVehicle()?.marca }}
                {{ getDefaultVehicle()?.modelo }}
                {{ getDefaultVehicle()?.motor }}
              </span>
            </div>
          </div>
          <div class="flex flex-col space-y-2">
            <button
              (click)="clearDefaultVehicle()"
              class="text-black underline text-sm hover:text-gray-700"
            >
              Cambiar Vehículo
            </button>
            <button
              (click)="clearDefaultVehicle()"
              class="text-black underline text-sm hover:text-gray-700"
            >
              Comprar Sin Vehículo
            </button>
          </div>
        </div>

        <!-- Sección Inferior: Información de la Batería -->
        <div class="flex items-start justify-between">
          <div class="flex-1 pr-8">
            <h3 class="font-bold text-black uppercase text-lg mb-3">
              BATERÍA DE TU VEHÍCULO
            </h3>
            <p class="text-black text-sm leading-relaxed">
              Esta es la batería recomendada para tu vehículo actual. Por favor
              consulta tu manual del propietario para información completa. Las
              especificaciones pueden variar según el modelo y año de tu
              vehículo.
            </p>
          </div>

          <div class="flex space-x-8">
            <div class="text-center">
              <span class="text-appPrimary-500 font-bold text-lg block"
                >Grupo 24F</span
              >
              <span class="text-black text-xs">Grupo BCI</span>
            </div>
            <div class="text-center">
              <span class="text-black font-bold text-lg block">650 CCA</span>
              <span class="text-black text-xs">Amperios de Arranque</span>
            </div>
            <div class="text-center">
              <span class="text-appPrimary-500 font-bold text-lg block"
                >12V</span
              >
              <span class="text-black text-xs">Voltaje</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class VehicleBatteryRecommendation {
  @Input() categoryName: string = '';

  private vehicleService = inject(VehicleService);

  readonly carIcon = CarIcon;
  readonly batteryIcon = BatteryIcon;

  hasDefaultVehicle = computed(() => {
    return !!this.vehicleService.getDefaultVehicle();
  });

  getDefaultVehicle() {
    return this.vehicleService.getDefaultVehicle();
  }

  clearDefaultVehicle(): void {
    // Ambos botones hacen lo mismo: quitar el vehículo por defecto
    const vehiculoPorDefecto = this.vehicleService.getDefaultVehicle();
    this.vehicleService.setDefaultVehicle(vehiculoPorDefecto?.id || 0, true);

    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }, 5);
  }
}
